import React, { ComponentType, FC, useEffect, useMemo } from 'react';
import { BrowserRouter, Switch, Route, RouteProps as BaseRouteProps, Redirect } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { LoadableComponent } from '@loadable/component';

// ROUTE
import { Router } from 'constant';

// COMPONENT
import { baseLoader, PageLoading } from 'components/Loading';
import UrlNotFoundPage from 'components/UrlNotFoundPage';
import { selectEnabledProductCatalog } from 'redux/featureFlag/selectors';
import { useSelector } from 'react-redux';

// HOOKS
import { useRequiredOnboarding } from 'hooks';

// PAGE
const MyServicePage = baseLoader(() => import('page/MyService'));
const RegisterService = baseLoader(() => import('page/RegisterService'));
const Welcome = baseLoader(() => import('page/Welcome'));
const VerifyAccount = baseLoader(() => import('page/VerifyAccount'));
const KeyInInformation = baseLoader(() => import('page/KeyInInformation'));
const ConfirmDetailsMySelf = baseLoader(() => import('page/ConfirmDetailsMySelf'));
const ConfirmDetailsNOK = baseLoader(() => import('page/ConfirmDetailsNOK'));
const ProfileAlreadyManaged = baseLoader(() => import('page/ProfileAlreadyManaged'));
const Profile = baseLoader(() => import('page/Profile'));
// const AuthenticatedProfile = withAuthenticationRequired(Profile);
const LinkAccountPage = baseLoader(() => import('page/Profile/LinkProfile'));
const PaymentPage = baseLoader(() => import('page/Payments'));
const GeneralTermCondition = baseLoader(() => import('page/TermCondition/General'));
const ProgramTermCondition = baseLoader(() => import('page/TermCondition/Program'));
const TermAndCondition = baseLoader(() => import('page/TermCondition/Term'));
const NewTermAndCondition = baseLoader(() => import('page/TermCondition/TermAndCondition'));
const InvoiceDetailPage = baseLoader(() => import('page/Invoices/Detail'));
const InvoiceList = baseLoader(() => import('page/Invoices/List'));
const PaymentCompletion = baseLoader(() => import('page/Invoices/PaymentCompletion'));
const MyCardsPage = baseLoader(() => import('page/MyCard'));
const PaymentVerification = baseLoader(() => import('page/PaymentVerification'));
const ProductCatalog = baseLoader(() => import('page/ProductCatalog'));
const ProductCatalogDetail = baseLoader(() => import('page/ProductCatalog/ProductCatalogDetail'));
const ProductCatalogCartReview = baseLoader(() => import('page/ProductCatalog/CartReview'));
const MedicalHistoryPage = baseLoader(() => import('page/MedicalHistory'));
const HealthDeclarationProfile = baseLoader(
  () => import('page/MyService/PurchasedServiceList/ServiceUserProgramSummary/ActiveAgeing/HealthDeclarationProfile')
);
const FAQ = baseLoader(() => import('page/Faq'));
const EGiroArrangements = baseLoader(() => import('page/eGiro'));
const ApplyEGiro = baseLoader(() => import('page/eGiro/apply/ApplyEGiro'));

interface RouteProps extends Omit<BaseRouteProps, 'component'> {
  component: LoadableComponent<unknown>;
}

// withAuthenticationRequired doesn't check auth error state, which causes an infinite redirect
// loop when an Auth0 rule (e.g. enrichUserRule) fails: Auth0 returns ?error=access_denied,
// the SDK clears the URL, then withAuthenticationRequired calls loginWithRedirect again.
// This HOC breaks that cycle by skipping the redirect when error is set.
const withRequiredAuth = (Component: ComponentType<Record<string, unknown>>) => {
  const RequiredAuth: FC = (props) => {
    const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();

    useEffect(() => {
      if (isLoading || isAuthenticated || error) return;

      loginWithRedirect({ appState: { returnTo: `${window.location.pathname}${window.location.search}` } });
    }, [isLoading, isAuthenticated, error, loginWithRedirect]);

    if (isAuthenticated) return <Component {...props} />;

    if (error) {
      window.location.href = Router.LoginIssue;

      return null;
    }

    return null;
  };

  return RequiredAuth;
};

const AuthenticatedRoute: FC<RouteProps> = ({ component, ...props }) => (
  <Route {...props} component={withRequiredAuth(component)} />
);

// Onboarding gate for every page that only makes sense once onboarding is done. Sits *inside*
// withRequiredAuth so an unauthenticated deep link still reaches loginWithRedirect first - see
// useRequiredOnboarding for why the isChecking phase must not render the page.
const withRequiredOnboarding = (Component: ComponentType<Record<string, unknown>>) => {
  const RequiredOnboarding: FC = (props) => {
    const { isChecking, shouldRedirectHome } = useRequiredOnboarding();

    // Same spinner the onboarding pages themselves show while they resolve, so a directly-hit URL
    // looks like it is loading rather than broken.
    if (isChecking) return <PageLoading />;

    if (shouldRedirectHome) return <Redirect to={Router.Home} />;

    return <Component {...props} />;
  };

  return RequiredOnboarding;
};

// AuthenticatedRoute + the onboarding gate. Onboarding URLs (Welcome and the wizard steps) are
// exempt, so they stay on plain AuthenticatedRoute. FAQ is public (BU-21859), so it is a plain Route.
const OnboardedRoute: FC<RouteProps> = ({ component, ...props }) => {
  // Memoised per page component - rebuilding the HOC chain on every render would remount the page.
  const guarded = useMemo(() => withRequiredAuth(withRequiredOnboarding(component)), [component]);

  return <Route {...props} component={guarded} />;
};

const AppRoute: FC<Record<string, unknown>> = () => {
  const env = process.env.REACT_APP_ENV;
  const isEnabledProductCatalog = useSelector(selectEnabledProductCatalog);

  const mainRoute = (
    <Switch>
      <Route exact component={() => <></>} path={Router.eGiroLanding} />
      <Route exact component={UrlNotFoundPage} path={Router.NotFoundPage} />
      <OnboardedRoute exact component={Profile} path={Router.Profile} />
      <AuthenticatedRoute exact component={Welcome} path={Router.Welcome} />
      <AuthenticatedRoute exact component={VerifyAccount} path={Router.VerifyAccount} />
      <AuthenticatedRoute exact component={KeyInInformation} path={Router.KeyInInformation} />
      <AuthenticatedRoute exact component={ConfirmDetailsMySelf} path={Router.ConfirmDetailsMySelf} />
      <AuthenticatedRoute exact component={ConfirmDetailsNOK} path={Router.ConfirmDetailsNOK} />
      <AuthenticatedRoute exact component={ProfileAlreadyManaged} path={Router.ProfileAlreadyManaged} />
      <OnboardedRoute exact component={RegisterService} path={Router.RegisterService} />
      <OnboardedRoute exact component={PaymentPage} path={Router.Payment} />
      <OnboardedRoute exact component={InvoiceList} path={Router.InvoiceList} />
      <OnboardedRoute exact component={InvoiceDetailPage} path={Router.InvoiceDetail} />
      <OnboardedRoute exact component={PaymentCompletion} path={Router.PaymentCompletion} />
      <OnboardedRoute exact component={LinkAccountPage} path={Router.LinkAccount} />
      <OnboardedRoute exact component={TermAndCondition} path={Router.TermAndCondition} />
      <OnboardedRoute exact component={NewTermAndCondition} path={Router.NewTermAndCondition} />
      <OnboardedRoute exact component={ProgramTermCondition} path={Router.ProgramTermAndCondition} />
      <OnboardedRoute exact component={GeneralTermCondition} path={Router.GeneralTermAndCondition} />
      <OnboardedRoute exact component={PaymentVerification} path={Router.PaymentVerification} />
      <OnboardedRoute component={HealthDeclarationProfile} path={Router.HealthDeclaration} />
      <OnboardedRoute component={MedicalHistoryPage} path={Router.MedicalHistory} />
      <OnboardedRoute component={MyCardsPage} path={Router.CardManagement} />
      <Route component={FAQ} path={Router.FAQ} />
      <OnboardedRoute exact component={EGiroArrangements} path={Router.eGiro} />
      <OnboardedRoute exact component={ApplyEGiro} path={Router.eGiroApply} />
      <OnboardedRoute component={MyServicePage} path={Router.MyService} />
      <AuthenticatedRoute component={Welcome} path={Router.Home} />
      <Redirect to={Router.NotFoundPage} />
    </Switch>
  );

  const mainRouteWithProductCatalog = (
    <Switch>
      <Route exact component={ProductCatalog} path={Router.Home} />
      <Route component={ProductCatalogDetail} path={Router.ProductDetail} />
      <Route component={ProductCatalog} path={Router.ServiceTypeDetail} />
      <Route exact component={() => <></>} path={Router.eGiroLanding} />
      <Route exact component={UrlNotFoundPage} path={Router.NotFoundPage} />
      <OnboardedRoute exact component={ProductCatalogCartReview} path={Router.ProductCatalogCartReview} />
      <OnboardedRoute exact component={Profile} path={Router.Profile} />
      <AuthenticatedRoute exact component={Welcome} path={Router.Welcome} />
      <AuthenticatedRoute exact component={VerifyAccount} path={Router.VerifyAccount} />
      <AuthenticatedRoute exact component={KeyInInformation} path={Router.KeyInInformation} />
      <AuthenticatedRoute exact component={ConfirmDetailsMySelf} path={Router.ConfirmDetailsMySelf} />
      <AuthenticatedRoute exact component={ConfirmDetailsNOK} path={Router.ConfirmDetailsNOK} />
      <AuthenticatedRoute exact component={ProfileAlreadyManaged} path={Router.ProfileAlreadyManaged} />
      <OnboardedRoute exact component={RegisterService} path={Router.RegisterService} />
      <OnboardedRoute exact component={PaymentPage} path={Router.Payment} />
      <OnboardedRoute exact component={InvoiceList} path={Router.InvoiceList} />
      <OnboardedRoute exact component={InvoiceDetailPage} path={Router.InvoiceDetail} />
      <OnboardedRoute exact component={PaymentCompletion} path={Router.PaymentCompletion} />
      <OnboardedRoute exact component={LinkAccountPage} path={Router.LinkAccount} />
      <OnboardedRoute exact component={TermAndCondition} path={Router.TermAndCondition} />
      <OnboardedRoute exact component={NewTermAndCondition} path={Router.NewTermAndCondition} />
      <OnboardedRoute exact component={ProgramTermCondition} path={Router.ProgramTermAndCondition} />
      <OnboardedRoute exact component={GeneralTermCondition} path={Router.GeneralTermAndCondition} />
      <OnboardedRoute exact component={PaymentVerification} path={Router.PaymentVerification} />
      <OnboardedRoute component={HealthDeclarationProfile} path={Router.HealthDeclaration} />
      <OnboardedRoute component={MedicalHistoryPage} path={Router.MedicalHistory} />
      <OnboardedRoute component={MyCardsPage} path={Router.CardManagement} />
      <Route component={FAQ} path={Router.FAQ} />
      <OnboardedRoute exact component={EGiroArrangements} path={Router.eGiro} />
      <OnboardedRoute exact component={ApplyEGiro} path={Router.eGiroApply} />
      <OnboardedRoute component={MyServicePage} path={Router.MyService} />
      <Redirect to={Router.NotFoundPage} />
    </Switch>
  );

  if (env === 'test') {
    return <BrowserRouter>{mainRoute}</BrowserRouter>;
  }

  if (isEnabledProductCatalog) return mainRouteWithProductCatalog;

  return mainRoute;
};

export default AppRoute;
